import { useMemo, useCallback, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  column: string;
  direction: SortDirection;
}

export interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
}

export interface ColumnDef<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  cell?: (row: T, index: number) => ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  keyExtractor: (row: T) => string;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  pagination?: PaginationState;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  loading?: boolean;
  emptyMessage?: string;
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  filterPlaceholder?: string;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onSelectionChange?: (keys: Set<string>) => void;
  onRowClick?: (row: T) => void;
  className?: string;
  /** Reduces cell padding and font sizes for a denser layout */
  compact?: boolean;
  /** Accessible label for the data table (used by screen readers) */
  'aria-label'?: string;
  /** Test identifier for automation (Playwright). */
  'data-testid'?: string;
}

function SortIcon({ direction }: { direction: SortDirection }) {
  return (
    <span className="ml-1 inline-flex flex-col" aria-hidden="true">
      <svg
        className={cn('h-3 w-3', direction === 'asc' ? 'text-brand-600' : 'text-gray-300')}
        viewBox="0 0 12 12"
        fill="currentColor"
      >
        <path d="M6 2l4 4H2z" />
      </svg>
      <svg
        className={cn('h-3 w-3 -mt-1', direction === 'desc' ? 'text-brand-600' : 'text-gray-300')}
        viewBox="0 0 12 12"
        fill="currentColor"
      >
        <path d="M6 10l4-4H2z" />
      </svg>
    </span>
  );
}

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  sort,
  onSortChange,
  pagination,
  onPageChange,
  onPageSizeChange,
  loading = false,
  emptyMessage = 'No data available',
  filterValue,
  onFilterChange,
  filterPlaceholder = 'Filter...',
  selectable = false,
  selectedKeys,
  onSelectionChange,
  onRowClick,
  className,
  compact = false,
  'aria-label': ariaLabel = 'Data table',
  'data-testid': testId,
}: DataTableProps<T>) {
  const handleSort = useCallback(
    (columnId: string) => {
      if (!onSortChange) return;
      if (sort?.column === columnId) {
        const nextDirection: SortDirection =
          sort.direction === 'asc' ? 'desc' : sort.direction === 'desc' ? null : 'asc';
        onSortChange({ column: columnId, direction: nextDirection });
      } else {
        onSortChange({ column: columnId, direction: 'asc' });
      }
    },
    [sort, onSortChange],
  );

  const allSelected = useMemo(() => {
    if (!selectable || data.length === 0) return false;
    return data.every((row) => selectedKeys?.has(keyExtractor(row)));
  }, [data, selectedKeys, selectable, keyExtractor]);

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(data.map(keyExtractor)));
    }
  }, [allSelected, data, keyExtractor, onSelectionChange]);

  const handleSelectRow = useCallback(
    (key: string) => {
      if (!onSelectionChange || !selectedKeys) return;
      const next = new Set(selectedKeys);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      onSelectionChange(next);
    },
    [selectedKeys, onSelectionChange],
  );

  const totalPages = pagination ? Math.ceil(pagination.total / pagination.pageSize) : 1;

  return (
    <div className={cn('w-full', className)} data-testid={testId || 'data-table'}>
      {/* Filter */}
      {onFilterChange && (
        <div className="mb-4">
          <input
            type="search"
            value={filterValue ?? ''}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder={filterPlaceholder}
            className="h-9 w-full max-w-sm rounded-lg border border-border bg-white px-3 text-sm placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
            aria-label={filterPlaceholder}
          />
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border dark:border-border-dark">
        <table
          className="w-full text-left text-sm"
          role="grid"
          aria-label={ariaLabel}
          data-testid={testId ? `${testId}-table` : 'data-table-grid'}
        >
          <thead>
            <tr className="border-b border-border bg-surface-secondary dark:border-border-dark dark:bg-surface-dark-tertiary">
              {selectable && (
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.id}
                  className={cn(
                    compact ? 'px-3 py-2 text-xs' : 'px-4 py-3',
                    'font-medium text-gray-600 dark:text-gray-300',
                    col.align === 'center' && 'text-center',
                    col.align === 'right' && 'text-right',
                    col.sortable &&
                      'cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100',
                  )}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={col.sortable ? () => handleSort(col.id) : undefined}
                  onKeyDown={
                    col.sortable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSort(col.id);
                          }
                        }
                      : undefined
                  }
                  tabIndex={col.sortable ? 0 : undefined}
                  aria-sort={
                    sort?.column === col.id
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : sort.direction === 'desc'
                          ? 'descending'
                          : 'none'
                      : undefined
                  }
                  role={col.sortable ? 'columnheader' : undefined}
                >
                  <span className="inline-flex items-center">
                    {col.header}
                    {col.sortable && (
                      <SortIcon direction={sort?.column === col.id ? sort.direction : null} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border dark:divide-border-dark">
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-4 py-12 text-center text-gray-500"
                >
                  <div className="flex items-center justify-center gap-2" aria-live="polite">
                    <svg
                      className="h-5 w-5 animate-spin text-brand-500"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Loading...
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-4 py-12 text-center text-gray-500 dark:text-gray-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const key = keyExtractor(row);
                const isSelected = selectedKeys?.has(key);
                return (
                  <tr
                    key={key}
                    className={cn(
                      'transition-colors [@media(hover:hover)]:hover:bg-surface-secondary dark:[@media(hover:hover)]:hover:bg-surface-dark-tertiary',
                      isSelected && 'bg-brand-50/50 dark:bg-brand-900/10',
                      onRowClick && 'cursor-pointer',
                    )}
                    onClick={() => onRowClick?.(row)}
                  >
                    {selectable && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected ?? false}
                          onChange={() => handleSelectRow(key)}
                          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          aria-label={`Select row ${rowIndex + 1}`}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={cn(
                          compact ? 'px-3 py-2' : 'px-4 py-3',
                          'text-gray-900 dark:text-gray-100',
                          col.align === 'center' && 'text-center',
                          col.align === 'right' && 'text-right',
                        )}
                      >
                        {col.cell
                          ? col.cell(row, rowIndex)
                          : col.accessorKey
                            ? String(row[col.accessorKey] ?? '')
                            : ''}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>
              Showing {Math.min((pagination.page - 1) * pagination.pageSize + 1, pagination.total)}{' '}
              to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
              {pagination.total}
            </span>
            {onPageSizeChange && (
              <select
                value={pagination.pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="h-8 rounded border border-border bg-white px-2 text-sm dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                aria-label="Rows per page"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            )}
          </div>
          <nav aria-label="Table pagination" className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="rounded-lg p-2 text-gray-500 hover:bg-surface-tertiary disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-surface-dark-tertiary"
              aria-label="Previous page"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <span className="px-3 text-sm text-gray-700 dark:text-gray-300">
              Page {pagination.page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={pagination.page >= totalPages}
              className="rounded-lg p-2 text-gray-500 hover:bg-surface-tertiary disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-surface-dark-tertiary"
              aria-label="Next page"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}
